import { Group, Stack, Text } from '@mantine/core';
import { isAdmin } from '~api';
import { api } from '~portal/api';
import { AnchorLink } from '~portal/components/common';
import {
  useAnnouncement,
  useLatestAnalystNote,
  useRecentActivity,
  useRecentNews,
  useTrackedPages,
  useWorkspace,
} from '~portal/data';
import { memberDisplayName } from '~portal/data/account';
import { firstNameOf, HOME } from '~portal/data/home';
import { RecentNews, RecentUpdates } from './Activity';
import { AnnouncementBanner, HomeIllustrativeNotice } from './Announcement';
import { ExplorePlatform } from './Explore';
import { Masthead } from './Masthead';
import { TrackedPages } from './Tracked';
import { WorkspaceSection } from './Workspace';

/**
 * @desc The platform home in Figma's order (`426:7806`, decision D-2): welcome masthead, the
 * platform announcement, Recent updates (the analyst note first), Recent news, Tracked pages,
 * Explore, Workspace, footer. News, follows, the announcement and the note are live; what is still
 * a fixture is badged. The KPI strip and the monthly chart came off Home (H3). Two-up rows collapse
 * to one column on narrow screens (client decision: no horizontal scrolling anywhere).
 */
export function HomePage() {
  const profile = api.users.me.useQuery().data;
  const workspace = useWorkspace();
  const announcement = useAnnouncement();
  const note = useLatestAnalystNote();
  const events = useRecentActivity();
  const news = useRecentNews();
  const tracked = useTrackedPages();
  const admin = isAdmin(profile);
  const fullName = profile
    ? memberDisplayName({
        firstName: profile.name.first,
        lastName: profile.name.last,
        email: profile.email,
      })
    : '';

  return (
    <Stack gap="xl" p="lg" maw={1280} mx="auto" w="100%">
      <Masthead
        firstName={firstNameOf(profile)}
        fullName={fullName}
        workspaceName={workspace.name}
        workspaceNameIsIllustrative={workspace.nameIsIllustrative}
        isAdmin={admin}
      />
      <AnnouncementBanner query={announcement} />
      <HomeIllustrativeNotice
        fixtureEvents={!events.error && (events.data?.length ?? 0) === 0}
        workspaceNameIllustrative={workspace.nameIsIllustrative}
      />
      <RecentUpdates note={note} events={events} />
      <RecentNews news={news} />
      <TrackedPages tracked={tracked} />
      <ExplorePlatform cards={HOME.explore} />
      <WorkspaceSection name={workspace.name} members={workspace.members} />
      <HomeFooter showAdminLink={admin} />
    </Stack>
  );
}

/**
 * @desc The small right-aligned link row at the foot of the home frame (Figma `426:7806`) — the
 * only place the admin link lives (H10). Documentation and the terms have no destination yet, so
 * they render as visibly inert text rather than as links that go nowhere.
 */
export function HomeFooter({ showAdminLink }: { showAdminLink: boolean }) {
  return (
    <Group
      component="footer"
      justify="flex-end"
      gap="lg"
      wrap="wrap"
      aria-label="Site links"
      data-testid="home-footer"
    >
      <AnchorLink to="/account" size="sm">
        Account Settings
      </AnchorLink>
      {showAdminLink && (
        <AnchorLink to="/admin/upload" size="sm">
          Admin Panel
        </AnchorLink>
      )}
      <Text size="sm" c="dimmed" aria-disabled="true" title="Coming soon">
        Documentation
      </Text>
      <Text size="sm" c="dimmed" aria-disabled="true" title="Coming soon">
        Terms &amp; Conditions
      </Text>
    </Group>
  );
}
